import {
  ConditionDefinition,
  SelectInputFieldDeclaration,
  SelectInputItem,
} from '@fmnts/declarative-forms';
import { map, MonoTypeOperatorFunction, Observable, switchMap } from 'rxjs';

type ItemConditions = SelectInputFieldDeclaration['itemConditions'];

interface ItemWithCondition {
  item: SelectInputItem;
  condition?: ConditionDefinition;
}

/**
 * @param changesSelector
 * A function that returns an `Observable` for each value emitted by the
 * source `Observable`, which is then used to filter the items upon emission
 * from the returned `Observable`.
 *
 * @param predicate
 * A function for determining if an item meets a specified condition.
 *
 * @returns
 * An `Observable` that emits items from the source `Observable` that fulfills the
 * given `predicate`.
 */
export function filterSelectItems<T>(
  changesSelector: (items: readonly ItemWithCondition[]) => Observable<T>,
  predicate: (changes: T, item: ItemWithCondition) => boolean,
): MonoTypeOperatorFunction<readonly ItemWithCondition[]> {
  return switchMap((items) =>
    changesSelector(items).pipe(
      map((changes) => items.filter((i) => predicate(changes, i))),
    ),
  );
}

/**
 * @param items Array of items
 * @param conditions Object of conditions per item
 * @param matchingKey The property name inside an item used to look for in the conditions
 *
 * @returns
 * Maps the items with the given conditions by the given `matchingKey` and returns them as a
 * single data structure.
 */
export function toItemsWithCondition(
  items: readonly SelectInputItem[],
  conditions: ItemConditions,
  matchingKey = 'value',
): readonly ItemWithCondition[] {
  return items.map((item) => ({
    item,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    condition: conditions?.[item[matchingKey]],
  }));
}
